import { z } from 'zod';

/**
 * The single reader of `import.meta.env`. Validates required variables at module
 * load so misconfiguration fails fast and clearly (instructions/06_SECURITY.md).
 * No other module may read `import.meta.env` directly.
 */
const schema = z.object({
  VITE_SUPABASE_URL: z.string().url('VITE_SUPABASE_URL must be a valid URL'),
  VITE_SUPABASE_ANON_KEY: z.string().min(1, 'VITE_SUPABASE_ANON_KEY is required'),
  // Dev-only: enables an email+password sign-in for local testing without an SMS
  // provider. Never set this in production — phone OTP is the real auth path.
  VITE_DEV_EMAIL_AUTH: z.enum(['true', 'false']).optional(),
  // Firebase Cloud Messaging (Phase 2). All optional — push is a progressive
  // enhancement; the app runs fully without these. Values are public-by-design.
  VITE_FIREBASE_API_KEY: z.string().optional(),
  VITE_FIREBASE_AUTH_DOMAIN: z.string().optional(),
  VITE_FIREBASE_PROJECT_ID: z.string().optional(),
  VITE_FIREBASE_MESSAGING_SENDER_ID: z.string().optional(),
  VITE_FIREBASE_APP_ID: z.string().optional(),
  VITE_FIREBASE_VAPID_KEY: z.string().optional(),
});

const parsed = schema.safeParse(import.meta.env);

if (!parsed.success) {
  const issues = parsed.error.issues.map((i) => `- ${i.path.join('.')}: ${i.message}`).join('\n');
  throw new Error(
    `Invalid environment configuration. Check web/.env.local against .env.example:\n${issues}`,
  );
}

const d = parsed.data;

/** Full Firebase config only when every required value is present; else null. */
const firebase =
  d.VITE_FIREBASE_API_KEY &&
  d.VITE_FIREBASE_AUTH_DOMAIN &&
  d.VITE_FIREBASE_PROJECT_ID &&
  d.VITE_FIREBASE_MESSAGING_SENDER_ID &&
  d.VITE_FIREBASE_APP_ID &&
  d.VITE_FIREBASE_VAPID_KEY
    ? {
        apiKey: d.VITE_FIREBASE_API_KEY,
        authDomain: d.VITE_FIREBASE_AUTH_DOMAIN,
        projectId: d.VITE_FIREBASE_PROJECT_ID,
        messagingSenderId: d.VITE_FIREBASE_MESSAGING_SENDER_ID,
        appId: d.VITE_FIREBASE_APP_ID,
        vapidKey: d.VITE_FIREBASE_VAPID_KEY,
      }
    : null;

export const env = Object.freeze({
  supabaseUrl: d.VITE_SUPABASE_URL,
  supabaseAnonKey: d.VITE_SUPABASE_ANON_KEY,
  // Email+password login toggle. Gated on VITE_DEV_EMAIL_AUTH alone so it can be
  // turned on for the deployed pilot (phone-OTP needs an SMS provider we don't
  // have yet). SECURITY: this exposes public email self-registration wherever the
  // flag is set, bypassing phone-OTP — keep it 'true' only on the private pilot
  // build and OFF for real launch. Tracked for security-controls review.
  devEmailAuth: d.VITE_DEV_EMAIL_AUTH === 'true',
  firebase,
  pushEnabled: firebase !== null,
  isDev: import.meta.env.DEV,
  isProd: import.meta.env.PROD,
});
